import React from 'react';
import { t } from '../../common/i18n';
import { AnalyticsEventOrigins } from '../../common/utils/analyticsUtils';
import { Grid } from '../Grid/Grid';
import {
  HorizontalMobileBroadbandSubscriptions,
  type HorizontalMobileBroadbandSubscriptionsProps,
} from './HorizontalMobileBroadbandSubscriptions';
import { addOfferToBasket } from './SubscriptionUtils';
import type { SubCardWithAnalyticsV4Props } from './subscriptionCardsAnalyticsUtils';
import type { OrderProps, PublicPageOrderProps, SubscriptionCardWithModel } from './types';
import type { OnlineModel } from '../../generated/api/onlineModel';
import type { MobileBroadbandSubCardProps } from '../SubscriptionCard/SubscriptionCard';

export interface HorizontalMobileBroadbandPublicSubscriptionsProps {
  subscriptionCards: MobileBroadbandSubCardProps[];
  salesProductOnlineModels?: OnlineModel[];
  renderComponent?: React.ComponentType<HorizontalMobileBroadbandSubscriptionsProps>;
  onOrder?: (p: PublicPageOrderProps | OrderProps) => void;
  eventOrigin?: string;
  wide?: boolean;
  compact?: boolean;
  hideGroupHeaders?: boolean;
  showDescriptions?: boolean;
}

export const HorizontalMobileBroadbandPublicSubscriptions = ({
  subscriptionCards,
  salesProductOnlineModels,
  renderComponent,
  onOrder = addOfferToBasket,
  eventOrigin = AnalyticsEventOrigins.PUBLIC,
  wide,
  compact = false,
  hideGroupHeaders = false,
  showDescriptions = true,
}: HorizontalMobileBroadbandPublicSubscriptionsProps) => {
  const getCard = (cardName: string) => {
    const mobileCard = subscriptionCards?.find(card => card.cardName === cardName);
    if (!mobileCard) {
      return undefined;
    }

    if (mobileCard.publicPage && salesProductOnlineModels?.length) {
      const onlineModel = salesProductOnlineModels.find(
        model => model.onlineModelCode === mobileCard.publicPage?.onlineModelCode
      );

      if (onlineModel) {
        const analyticsCard = mobileCard as SubCardWithAnalyticsV4Props;
        const offerForCard =
          onlineModel.offers.find(offer => offer.offerCode === mobileCard.publicPage?.offerCode) ?? analyticsCard.offer;

        return {
          ...mobileCard,
          onlineModel,
          ...(offerForCard ? { offer: offerForCard } : {}),
        } as SubscriptionCardWithModel;
      }
    }

    if ((mobileCard as SubscriptionCardWithModel).onlineModel) {
      return mobileCard as SubscriptionCardWithModel;
    }

    return undefined;
  };

  const Component = renderComponent ?? HorizontalMobileBroadbandSubscriptions;
  const content = (
    <div className="of-subscriptions-main-container">
      <Component
        subscriptionCards={subscriptionCards}
        getCard={getCard}
        onOrder={onOrder}
        showDescriptions={showDescriptions}
        altDescription={t.DDRB('The monthly subscription fee also includes a router.')}
        eventOrigin={eventOrigin}
        compact={compact}
        hideGroupHeaders={hideGroupHeaders}
      />
    </div>
  );

  if (compact) {
    return content;
  }

  return <Grid colWidthL={wide ? 12 : 10}>{content}</Grid>;
};
